import { useEffect, useState } from "react";
import { Popover, Tooltip, Button } from "antd";
import { Link, useLocation } from "react-router-dom";
import {
  PlusOutlined,
  PlusSquareOutlined,
  BarChartOutlined,
  SelectOutlined,
  QrcodeOutlined,
  ShoppingCartOutlined,
  ShoppingOutlined,
  EditOutlined,
  ShopOutlined,
  DesktopOutlined,
  PlusCircleFilled, LinkOutlined,
} from "@ant-design/icons";
import { hasPermissions, Permission } from "../../../modules/Common/constants";

const generalShortCuts = [
  { title: "User", url: "/users/user/create", state: {}, icon: <PlusCircleFilled /> },
  { title: "Role", url: "/users/roles/create", state: {}, icon: <PlusCircleFilled /> },
  { title: "E-com Theme", url: "/store/ecomtheme", state: {}, icon: <LinkOutlined /> },
  {
    title: "Customer Facing Display",
    url: "/v2/customer-display",
    state: {},
    icon: <DesktopOutlined />,
    openInNewWindow: true,
  },
];

const salesShortCuts = [
  { title: "Customer", url: "/sale/customers/new", state: {}, icon: <PlusCircleFilled /> },
  { title: "Customer Group", url: "/sale/customer-groups/new", state: {}, icon: <PlusCircleFilled /> },
  { title: "Sale Order", url: "/v2/sale/sale-q-order", state: {}, icon: <PlusCircleFilled />, external: true },
  { title: "Sales Return", url: "/v2/sale/sale-q-order?directSaleReturnCreate=1", state: {}, icon: <PlusCircleFilled />, external: true },
  { title: "Receive Payment", url: "/sale/customers/payment", state: {}, icon: <PlusCircleFilled /> },
  { title: "Promotion", url: "/marketing/promotions/new", state: {}, icon: <PlusCircleFilled /> },
  // { title: "Add Sales Return", url: "/sale/sale-return" },
];

const purchaseShortCuts = [
  { title: "Supplier", url: "/inventory/suppliers/new", state: {}, icon: <PlusCircleFilled /> },
  { title: "Purchase Order", url: "/inventory/purchase/orders/new", state: {}, icon: <PlusCircleFilled /> },
  { title: "Purchase Receive", url: "/inventory/purchase/receives/create", state: {}, icon: <PlusCircleFilled /> },
  { title: "Purchase Return", url: "/inventory/purchase/order/return?independent=1", state: {}, icon: <PlusCircleFilled /> },
  { title: "Stock Transfer", url: "/inventory/transfer/stocks/new", state: {}, icon: <PlusCircleFilled /> },
  { title: "Store Channel", url: "/inventory/warehouse/new", state: {}, icon: <PlusCircleFilled /> },
];
const reportsShortCuts = [
  { title: "Sale Summary", url: "/reporting/purchasing-inventory/sales/sales-summary", state: {}, icon: <LinkOutlined /> },
  { title: "Daily Summary", url: "/reporting/financial-reporting/daywise-summary-report", state: {}, icon: <LinkOutlined /> },
  { title: "Payment Received", url: "/reporting/financial-reporting/payment-received-report", state: {}, icon: <LinkOutlined /> },
  { title: "Customer Payment Logs Report", url: "/reporting/financial-reporting/payment-logs-report", state: {}, icon: <SelectOutlined className="px-2" /> },
];
const productShortCuts = [
  { title: "Category", url: "/catalog/categories/new", state: {}, icon: <PlusCircleFilled /> },
  { title: "Brand", url: "/catalog/brands/new", state: {}, icon: <PlusCircleFilled /> },
  { title: "Product", url: "/catalog/products/new", state: {}, icon: <PlusCircleFilled /> },
  { title: "Product Group", url: "/catalog/product-groups/new", state: {}, icon: <PlusCircleFilled /> },
];

const defaultFrequentlyUsedShortcuts = [
  { title: "Add product", url: "/catalog/products/new" },
  { title: "Sale order", url: "/v2/sale/sale-q-order", external: true },
  { title: "Purchase Order", url: "/inventory/purchase/orders/new" },
];

const frequentlyUsedStorageKey = "quickCreateFrequentlyUsedV2";
const shortcutClickCountsStorageKey = "quickCreateShortcutClickCounts";
const frequentlyUsedClickThreshold = 3;
const shortcutTitleOverrides = {
  "/reporting/purchasing-inventory/sales/sales-summary": "Sale Summary",
  "/reporting/financial-reporting/daywise-summary-report": "Daily Summary",
  "/reporting/financial-reporting/payment-received-report": "Payment Received",
};

const openShortcutInNewWindow = (url) => {
  const baseUrl = window.location.origin;
  const fullUrl = `${baseUrl}${url}`;
  const newWindow = window.open(fullUrl, "customer-display", "width=1920,height=1080,left=1920,top=0,scrollbars=yes,resizable=yes");

  if (!newWindow || newWindow.closed || typeof newWindow.closed === "undefined") {
    window.open(fullUrl, "_blank");
  }
};

function FrequentlyUsedIcon({ url, iconType }) {
  return iconType === "link" || url.startsWith("/reporting/") || url === "/store/ecomtheme" ? <LinkOutlined /> : <PlusCircleFilled />;
}

function ShortcutSection({ title: sectionTitle, shortcuts, onShortcutClick }) {
  return (
    <ul className="quick-create-section m-0">
      <li className="shortcut-list">
        <h6 className="quick-create-section-title d-flex">
          <span>{sectionTitle}</span>
        </h6>
      </li>
      {shortcuts.map(({ url, icon, title, state, openInNewWindow, external }, index) => (
        <li key={`${title}-${index}`} className="shortcut-list">
          {openInNewWindow ? (
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                onShortcutClick({ title, url, external, openInNewWindow, iconType: sectionTitle === "Reports" ? "link" : "plus" });
                openShortcutInNewWindow(url);
              }}
              className="quick-create-link d-flex"
            >
              {icon}
              <span>{title}</span>
            </a>
          ) : external ? (
            <a
              href={url}
              className="quick-create-link d-flex"
              onClick={() => onShortcutClick({ title, url, external, iconType: sectionTitle === "Reports" ? "link" : "plus" })}
            >
              {icon}
              <span>{title}</span>
            </a>
          ) : (
            <Link
              to={{ pathname: url, state }}
              className="quick-create-link d-flex"
              onClick={() => onShortcutClick({ title, url, iconType: sectionTitle === "Reports" ? "link" : "plus" })}
            >
              {icon}
              <span>{title}</span>
            </Link>
          )}
        </li>
      ))}
    </ul>
  );
}

export default function QuickCreate() {
  const canViewReports = hasPermissions([Permission.ReportingView])[0];
  const location = useLocation();
  const [isQuickCreateOpen, setIsQuickCreateOpen] = useState(false);
  const [frequentlyUsedShortcuts, setFrequentlyUsedShortcuts] = useState(() => {
    try {
      const savedShortcuts = JSON.parse(localStorage.getItem(frequentlyUsedStorageKey));
      return Array.isArray(savedShortcuts) && savedShortcuts.length === 3
        ? savedShortcuts.map((shortcut) => ({ ...shortcut, title: shortcutTitleOverrides[shortcut.url] || shortcut.title }))
        : defaultFrequentlyUsedShortcuts;
    } catch {
      return defaultFrequentlyUsedShortcuts;
    }
  });

  useEffect(() => {
    setIsQuickCreateOpen(false);
  }, [location.pathname, location.search]);

  const closeQuickCreate = () => setIsQuickCreateOpen(false);

  const promoteShortcut = (shortcut) => {
    const existingIndex = frequentlyUsedShortcuts.findIndex(({ url }) => url === shortcut.url);
    if (existingIndex === 0) return;

    const nextShortcuts = [...frequentlyUsedShortcuts];
    if (existingIndex > 0) {
      [nextShortcuts[0], nextShortcuts[existingIndex]] = [shortcut, nextShortcuts[0]];
    } else {
      nextShortcuts[0] = shortcut;
    }
    localStorage.setItem(frequentlyUsedStorageKey, JSON.stringify(nextShortcuts));
    setFrequentlyUsedShortcuts(nextShortcuts);
  };

  const registerShortcutClick = (shortcut) => {
    closeQuickCreate();

    let clickCounts = {};
    try {
      clickCounts = JSON.parse(localStorage.getItem(shortcutClickCountsStorageKey)) || {};
    } catch {
      clickCounts = {};
    }

    const nextClickCount = (clickCounts[shortcut.url] || 0) + 1;
    clickCounts[shortcut.url] = nextClickCount;
    localStorage.setItem(shortcutClickCountsStorageKey, JSON.stringify(clickCounts));

    if (nextClickCount >= frequentlyUsedClickThreshold) {
      promoteShortcut(shortcut);
    }
  };

  const content = (
    <div className="quick-create-content">
      <div className="quick-create-frequent-title">Frequently Used</div>
      <div className="quick-create-frequent-links">
        {frequentlyUsedShortcuts.map(({ title, url, external, openInNewWindow, iconType }) =>
          openInNewWindow ? (
            <a
              key={title}
              href="#"
              className="quick-create-pill"
              onClick={(e) => {
                e.preventDefault();
                closeQuickCreate();
                openShortcutInNewWindow(url);
              }}
            >
              <FrequentlyUsedIcon url={url} iconType={iconType} /> {title}
            </a>
          ) : external ? (
            <a key={title} href={url} className="quick-create-pill" onClick={closeQuickCreate}>
              <FrequentlyUsedIcon url={url} iconType={iconType} /> {title}
            </a>
          ) : (
            <Link key={title} to={url} className="quick-create-pill" onClick={closeQuickCreate}>
              <FrequentlyUsedIcon url={url} iconType={iconType} /> {title}
            </Link>
          )
        )}
      </div>
      <div className="quick-create-panel">
        <ShortcutSection title="General" shortcuts={generalShortCuts} onShortcutClick={registerShortcutClick} />
        <ShortcutSection title="Product" shortcuts={productShortCuts} onShortcutClick={registerShortcutClick} />
        <ShortcutSection title="Purchase" shortcuts={purchaseShortCuts} onShortcutClick={registerShortcutClick} />
        <ShortcutSection title="Sales" shortcuts={salesShortCuts} onShortcutClick={registerShortcutClick} />
        {canViewReports ? <ShortcutSection icon={BarChartOutlined} title="Reports" shortcuts={reportsShortCuts} /> : null}

      </div>
    </div>
  );

  return (
    <Popover
      content={content}
      placement="bottomRight"
      trigger="click"
      visible={isQuickCreateOpen}
      onVisibleChange={setIsQuickCreateOpen}
      overlayClassName="quick-create-popover-overlay"
    >
      <Tooltip title="Quick Create" placement="bottom">
        <Button
          type="primary"
          icon={<PlusOutlined className="mb-5" />}
          aria-label="Quick Create"
          className="quick-create p-0 border-0"
          style={{ background: "rgb(234, 30, 81)", borderRadius: "5px" }}
        />
      </Tooltip>
    </Popover>
  );
}
